import { Box, Checkbox, Stack, TextField, Typography } from "@mui/material"
import { useEffect, useState } from "react"

import DialogProvider from "@components/dialog_provider"
import { LIGHT_BLUE, PRIMARY } from "@constants/colors"
import { useChangelogPageContext } from "@contexts/main/changelog_page_context"
import {
  publishAnnouncementReq,
  scheduleAnnouncementReq,
  updateAnnouncementReq
} from "@domain/changelog"
import { ChangelogChannel } from "@models/changelog"
import { showErrorToast, showSuccessToast } from "@utils/toast"

const CHANNELS: {
  key: ChangelogChannel
  labelKey: string
  descKey: string
}[] = [
  {
    key: ChangelogChannel.EMAIL,
    labelKey: "changelog_channel_email",
    descKey: "changelog_channel_email_desc"
  },
  {
    key: ChangelogChannel.WHATSAPP,
    labelKey: "changelog_channel_whatsapp",
    descKey: "changelog_channel_whatsapp_desc"
  },
  {
    key: ChangelogChannel.PUSH,
    labelKey: "changelog_channel_push",
    descKey: "changelog_channel_push_desc"
  }
]

export default function SendNotificationDialog() {
  const {
    translate,
    isSendModalOpen,
    sendTarget,
    closeSendModal,
    refetchAnnouncements
  } = useChangelogPageContext()

  const [channels, setChannels] = useState<ChangelogChannel[]>([])
  const [timing, setTiming] = useState<"now" | "schedule">("now")
  const [scheduledAt, setScheduledAt] = useState<string>("")
  const [skipNotifications, setSkipNotifications] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isSendModalOpen && sendTarget) {
      setChannels(sendTarget.channels || [])
      setTiming("now")
      setScheduledAt("")
      setSkipNotifications(false)
    }
  }, [isSendModalOpen, sendTarget])

  const toggle = (key: ChangelogChannel) => {
    setChannels((prev) =>
      prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]
    )
  }

  const onSubmit = async () => {
    if (!sendTarget) {
      return
    }
    const willNotify = timing === "schedule" || !skipNotifications
    if (willNotify && channels.length === 0) {
      showErrorToast(translate("changelog_pick_channel"))
      return
    }
    if (timing === "schedule" && !scheduledAt) {
      showErrorToast(translate("changelog_pick_datetime"))
      return
    }
    setLoading(true)
    try {
      // Persist any last-minute channel change before sending.
      await updateAnnouncementReq({ _id: sendTarget._id, channels })
      const res =
        timing === "now"
          ? await publishAnnouncementReq({
              _id: sendTarget._id,
              notify: !skipNotifications
            })
          : await scheduleAnnouncementReq({
              _id: sendTarget._id,
              scheduledAt: new Date(scheduledAt).toISOString()
            })
      if (!res.isSuccess) {
        showErrorToast(res.errorMessage || translate("changelog_send_failed"))
        return
      }
      showSuccessToast(
        timing === "now"
          ? translate("changelog_sent")
          : translate("changelog_scheduled_ok")
      )
      refetchAnnouncements()
      closeSendModal()
    } finally {
      setLoading(false)
    }
  }

  return (
    <DialogProvider
      title={translate("changelog_send_notification")}
      isOpen={isSendModalOpen}
      setClose={closeSendModal}
      size="small"
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: closeSendModal
      }}
      rightButton={{
        text:
          timing === "now"
            ? translate("changelog_send_now")
            : translate("changelog_schedule"),
        color: "primary",
        onClick: onSubmit,
        loading
      }}
    >
      <Box>
        <Typography
          sx={{ color: "text.secondary", fontSize: "0.85rem", mb: 2 }}
        >
          {translate("changelog_send_subtitle")}
        </Typography>

        <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, mb: 1 }}>
          {translate("changelog_channels_label")}
        </Typography>
        <Stack gap={1} sx={{ mb: 2.5 }}>
          {CHANNELS.map((ch) => {
            const checked = channels.includes(ch.key)
            return (
              <Stack
                key={ch.key}
                direction="row"
                alignItems="center"
                onClick={() => toggle(ch.key)}
                sx={{
                  cursor: "pointer",
                  border: 1,
                  borderColor: checked ? PRIMARY : "divider",
                  background: checked ? LIGHT_BLUE : "white",
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 1
                }}
              >
                <Checkbox
                  checked={checked}
                  size="small"
                  sx={{ p: 0.5, mr: 1 }}
                />
                <Box>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
                    {translate(ch.labelKey)}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.78rem", color: "text.secondary" }}
                  >
                    {translate(ch.descKey)}
                  </Typography>
                </Box>
              </Stack>
            )
          })}
        </Stack>

        {timing === "now" && (
          <Stack
            direction="row"
            alignItems="center"
            onClick={() => setSkipNotifications((value) => !value)}
            sx={{ cursor: "pointer", mb: 2.5 }}
          >
            <Checkbox
              checked={skipNotifications}
              size="small"
              sx={{ p: 0.5, mr: 1 }}
            />
            <Typography sx={{ fontSize: "0.88rem", fontWeight: 500 }}>
              {translate("changelog_skip_notifications")}
            </Typography>
          </Stack>
        )}

        <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, mb: 1 }}>
          {translate("changelog_timing_label")}
        </Typography>
        <Stack
          direction="row"
          gap={1}
          sx={{ mb: timing === "schedule" ? 2 : 0 }}
        >
          {(["now", "schedule"] as const).map((opt) => {
            const active = timing === opt
            return (
              <Box
                key={opt}
                onClick={() => setTiming(opt)}
                sx={{
                  flex: 1,
                  textAlign: "center",
                  cursor: "pointer",
                  px: 2,
                  py: 1,
                  borderRadius: 1.5,
                  border: 1,
                  borderColor: active ? PRIMARY : "divider",
                  background: active ? LIGHT_BLUE : "white",
                  color: active ? PRIMARY : "text.primary",
                  fontWeight: active ? 600 : 500,
                  fontSize: "0.88rem"
                }}
              >
                {opt === "now"
                  ? translate("changelog_send_now")
                  : translate("changelog_schedule")}
              </Box>
            )
          })}
        </Stack>

        {timing === "schedule" && (
          <TextField
            fullWidth
            size="small"
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
          />
        )}
      </Box>
    </DialogProvider>
  )
}
